/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Row as ReactTableRow } from "react-table";
import { Box, Button, Col, Heading, Row, theme } from "@nordcloud/gnui";
import {
  NotificationListDocument,
  useMarkNotificationsAsReadMutation,
  useNotificationListQuery,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  InlineSpinner,
  ReactTable,
  TableLoader,
  UniversalWrap,
} from "~/components";
import { ERROR_TEXT } from "~/constants";
import { notificationLimit } from "~/context/Notifications";
import { INotificationUI } from "~/models/notifications";
import { showError } from "~/services/toast";
import { sortByCreatedAt } from "~/tools";
import { NotificationDetails } from "./NotificationDetails";
import { NotificationListColumns } from "./NotificationListColumns";

export function NotificationPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoadMoreDisabled, setIsLoadMoreDisabled] = useState(true);
  const [notification, setNotification] = useState<INotificationUI>({
    id: "",
    title: "",
    description: "",
    targetView: "",
  });

  const { data, loading, error, fetchMore, client } = useNotificationListQuery({
    variables: { limit: notificationLimit },
    notifyOnNetworkStatusChange: true,
  });

  const [markNotification] = useMarkNotificationsAsReadMutation();

  const onMarkNotification = useCallback(
    async (id: string) => {
      try {
        const response = await markNotification({
          variables: { ids: [id] },
        });
        if (response?.data?.markNotificationsAsRead) {
          client.writeQuery({
            query: NotificationListDocument,
            data: {
              notifications: {
                cursor: data?.notifications?.cursor,
                __typename: data?.notifications?.__typename,
                notifications: data?.notifications?.notifications?.map(
                  (item) => {
                    if (item.id === id) {
                      return {
                        ...item,
                        read: true,
                      };
                    }
                    return item;
                  }
                ),
              },
            },
          });
        }
      } catch {
        showError(ERROR_TEXT.default);
      }
    },
    [markNotification, client, data]
  );

  useEffect(() => {
    setIsLoadMoreDisabled(data?.notifications?.cursor === "");
  }, [setIsLoadMoreDisabled, data]);

  const columns = useMemo(() => NotificationListColumns(), []);

  const sortedRows = useMemo(() => {
    const rows = data?.notifications?.notifications || [];
    return sortByCreatedAt(rows, true);
  }, [data]);

  const handleFetchMore = () => {
    void fetchMore({
      variables: {
        cursor: data?.notifications?.cursor,
        limit: notificationLimit,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousEntry = previousResult.notifications;
        const newNotifications =
          fetchMoreResult?.notifications?.notifications ?? [];
        const newCursor = fetchMoreResult?.notifications?.cursor ?? "";
        setIsLoadMoreDisabled(newCursor === "");
        return {
          notifications: {
            cursor: newCursor,
            __typename: previousEntry?.__typename ?? "NotificationsResponse",
            notifications: [
              ...newNotifications,
              ...(previousEntry?.notifications ?? []),
            ],
          },
        };
      },
    });
  };

  const openNotification = (selected: Notification) => {
    setIsSidebarOpen(true);
    setNotification({
      id: selected.id,
      title: selected.title,
      description: selected.details,
      targetView: selected.targetView,
    });
    void onMarkNotification(selected.id);
  };

  const handleRowKeyPress = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    selected: Notification
  ) => {
    if (event.key === "Enter") {
      openNotification(selected);
    }
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const isFirstLoading = loading && data == null;
  const isFetchMoreLoading = loading && data != null;

  return (
    <>
      <Row>
        <Col>
          <BreadcrumbsBox title="Notifications" />
          <Box margin="spacing04">
            <Heading level={5}>List of Notifications</Heading>
            <UniversalWrap
              errorProps={{ error }}
              loaderProps={{ loading: isFirstLoading, Component: Loader }}
            >
              <ReactTable
                striped
                columns={columns}
                data={sortedRows}
                getRowProps={(row: ReactTableRow<Notification>) => ({
                  onClick: () => openNotification(row.original),
                  onKeyPress: (
                    event: React.KeyboardEvent<HTMLTableRowElement>
                  ) => handleRowKeyPress(event, row.original),
                  className: "interactive",
                  tabIndex: 0,
                  role: "button",
                })}
              />
            </UniversalWrap>
            <InlineSpinner
              show={isFetchMoreLoading}
              css={{
                marginBottom: theme.spacing.spacing08,
              }}
            />
            <Button disabled={isLoadMoreDisabled} onClick={handleFetchMore}>
              Load more
            </Button>
          </Box>
        </Col>
      </Row>
      <NotificationDetails
        isOpen={isSidebarOpen}
        notification={notification}
        onClose={closeSidebar}
      />
    </>
  );
}

type Notification = NonNullable<
  NonNullable<
    NonNullable<
      ReturnType<typeof useNotificationListQuery>["data"]
    >["notifications"]
  >["notifications"]
>[number];

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={6}
      css={{
        marginBlock: theme.spacing.spacing08,
      }}
      rows={[
        {
          height: 60,
          count: 7,
          items: [
            {
              widthPercent: 8,
            },
            {
              widthPercent: 52,
            },
            {
              widthPercent: 20,
            },
            {
              widthPercent: 20,
            },
          ],
        },
      ]}
    />
  );
}
